import {
  Query,
  QueryTransferOptionsArgs,
  TransferDestinationInput,
  TransferDestinationTypeInput,
  TransferRouteDetailsInput,
  TransferSourceInput,
} from '@varo/graphql-schema-types';
import {
  resolveAccountRoutePolicy,
  resolveCardRoutePolicy,
} from 'features/feeBasedInstantTransfer/adapters/transferRoutePolicyAdapter';
import { ANALYTICS_SOURCES } from 'features/feeBasedInstantTransfer/helpers/analyticsFormatters';
import useOperationAnalytics from 'features/feeBasedInstantTransfer/hooks/useOperationAnalytics';
import FeeBasedTransferMaximumQuery from 'services/graphql/queries/MoveMoney/FeeBasedTransferMaximumQuery';
import FeeBasedTransferRouteDetailsQuery from 'services/graphql/queries/MoveMoney/FeeBasedTransferRouteDetailsQuery';
import type { IconKeyType } from 'shared/components/vxl/icon/Icon/Icon';
import { CombinedError, useQuery } from 'urql';

export interface TransferPrincipalLimit {
  minorUnits: number;
  formatted: string;
}

export interface ReadyTransferRoutePolicy {
  status: 'ready';
  speedDescription?: string;
  speedIcon?: IconKeyType;
  feePercentageText?: string;
  feeRate?: number;
  minimumPrincipal?: TransferPrincipalLimit;
  maximumPrincipal?: TransferPrincipalLimit;
  minimumMessage?: string;
  maximumMessage?: string;
  limitErrorMessage?: string;
}

export interface UnavailableTransferRoutePolicy {
  status: 'unavailable';
  message?: string;
  onRetry?: () => void;
  requestError?: CombinedError;
}

export type TransferRoutePolicy =
  | { status: 'idle' }
  | { status: 'loading' }
  | UnavailableTransferRoutePolicy
  | ReadyTransferRoutePolicy;

interface SelectedRoute {
  source?: TransferSourceInput;
  destination?: TransferDestinationInput;
}

export function useSelectedRoutePolicy({
  source,
  destination,
}: SelectedRoute): TransferRoutePolicy {
  const routeInput =
    source && destination ? { source, destination } : undefined;
  const shouldFetchMaximum =
    routeInput !== undefined &&
    destination?.type === TransferDestinationTypeInput.Account;

  const [routeDetailsResult, reexecuteRouteDetailsQuery] = useQuery<
    Pick<Query, 'transferRouteDetails'>,
    { input: TransferRouteDetailsInput | undefined }
  >({
    query: FeeBasedTransferRouteDetailsQuery,
    variables: { input: routeInput },
    pause: !routeInput,
    requestPolicy: 'network-only',
  });
  const [maximumResult, reexecuteMaximumQuery] = useQuery<
    Pick<Query, 'transferOptions'>,
    QueryTransferOptionsArgs
  >({
    query: FeeBasedTransferMaximumQuery,
    variables: {
      input: {
        sourceAccountId: source?.id,
        destinationAccountId: destination?.id,
        includeExternalDebitCardDestinations: true,
      },
    },
    pause: !shouldFetchMaximum,
    requestPolicy: 'network-only',
  });

  useOperationAnalytics(
    ANALYTICS_SOURCES.amount,
    'transfer_route_details',
    routeDetailsResult,
    routeDetailsResult.data?.transferRouteDetails,
    !routeInput,
  );
  useOperationAnalytics(
    ANALYTICS_SOURCES.amount,
    'transfer_maximum',
    maximumResult,
    maximumResult.data?.transferOptions,
    !shouldFetchMaximum,
  );

  if (!routeInput) {
    return { status: 'idle' };
  }
  if (
    routeDetailsResult.fetching ||
    (shouldFetchMaximum && maximumResult.fetching)
  ) {
    return { status: 'loading' };
  }

  const routeDetailsOutcome = {
    result: routeDetailsResult.data?.transferRouteDetails,
    hasRequestFailed: routeDetailsResult.error !== undefined,
  };
  const resolution = shouldFetchMaximum
    ? resolveAccountRoutePolicy(routeDetailsOutcome, {
        result: maximumResult.data?.transferOptions,
        hasRequestFailed: maximumResult.error !== undefined,
      })
    : resolveCardRoutePolicy(routeDetailsOutcome);
  if (resolution.policy.status === 'ready') {
    return resolution.policy;
  }

  const { hasRouteDetailsFailed, hasMaximumFailed } = resolution;
  const handleRetry = () => {
    if (hasRouteDetailsFailed) {
      reexecuteRouteDetailsQuery({ requestPolicy: 'network-only' });
    }
    if (hasMaximumFailed) {
      reexecuteMaximumQuery({ requestPolicy: 'network-only' });
    }
  };

  return {
    status: 'unavailable',
    message: resolution.policy.message,
    onRetry:
      hasRouteDetailsFailed || hasMaximumFailed ? handleRetry : undefined,
    requestError: routeDetailsResult.error ?? maximumResult.error,
  };
}
